"use client";

import {
	Area,
	AreaChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

interface TrendChartProps {
	data: Array<{
		date: string;
		models: Record<string, number>;
	}>;
	isLoading?: boolean;
}

const COLORS = [
	"#3b82f6", // blue
	"#ef4444", // red
	"#10b981", // green
	"#f59e0b", // amber
	"#8b5cf6", // purple
];

const CustomTooltip = ({
	active,
	payload,
	label,
}: {
	active?: boolean;
	payload?: Array<{ name: string; value: number; color: string }>;
	label?: string;
}) => {
	if (active && payload && payload.length) {
		return (
			<div className="rounded-lg border bg-popover text-popover-foreground p-3 shadow-sm">
				<p className="font-semibold text-sm">{label}</p>
				{payload.map((entry, index) => (
					<p key={index} className="text-xs text-muted-foreground">
						<span style={{ color: entry.color }}>●</span> {entry.name}:{" "}
						{entry.value.toLocaleString()}
					</p>
				))}
			</div>
		);
	}
	return null;
};

export function PopularityTrendChart({ data, isLoading }: TrendChartProps) {
	if (isLoading) {
		return (
			<div className="flex h-[350px] items-center justify-center">
				<div className="text-sm text-muted-foreground">Loading chart...</div>
			</div>
		);
	}

	if (!data || data.length === 0) {
		return (
			<div className="flex h-[350px] items-center justify-center">
				<div className="text-sm text-muted-foreground">No data available</div>
			</div>
		);
	}

	// Get all models
	const allModels = Array.from(
		new Set(data.flatMap((d) => Object.keys(d.models))),
	);

	return (
		<ResponsiveContainer width="100%" height={350}>
			<AreaChart
				data={data}
				margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
			>
				<defs>
					{allModels.map((model, index) => (
						<linearGradient
							key={`gradient-${index}`}
							id={`color-${model}`}
							x1="0"
							y1="0"
							x2="0"
							y2="1"
						>
							<stop
								offset="5%"
								stopColor={COLORS[index % COLORS.length]}
								stopOpacity={0.8}
							/>
							<stop
								offset="95%"
								stopColor={COLORS[index % COLORS.length]}
								stopOpacity={0.1}
							/>
						</linearGradient>
					))}
				</defs>
				<CartesianGrid strokeDasharray="3 3" />
				<XAxis dataKey="date" />
				<YAxis />
				<Tooltip content={<CustomTooltip />} />
				<Legend />
				{allModels.map((model, index) => (
					<Area
						key={model}
						type="monotone"
						dataKey={`models.${model}`}
						stackId="1"
						stroke={COLORS[index % COLORS.length]}
						fill={`url(#color-${model})`}
						name={model}
					/>
				))}
			</AreaChart>
		</ResponsiveContainer>
	);
}
